/**
 * @file index.ts
 * @description Core types and schema definitions for CountTheTime.
 * @author [F. Andy Seidl](https://www.linkedin.com/in/faseidl/)
 * @license UNLICENSED
 */

import { z } from 'zod';
import { TargetSpecSchema } from './relative-calendar.js';

/*======================================================================
 * Timing Direction & Operational Status Types
 *======================================================================*/

/**
 * Represents the operational tracking direction of a timer.
 *
 * @remarks
 * Supports countdown, count-up, and is forward-compatible with future behavior extensions.
 */
export type TimerDirection = 'COUNT_DOWN' | 'COUNT_UP' | 'AUTO_FLIP' | 'PERIODIC';

/**
 * Represents the operational lifecycle status of a timer.
 */
export type TimerStatus = 'RUNNING' | 'COMPLETED';

/*======================================================================
 * Time Units & Interval Segmentation Types
 *======================================================================*/

/**
 * Schema validating supported units for duration segmentation and presentation limits.
 */
export const TimeUnitSchema = z.enum([
  'years',
  'months',
  'days',
  'hours',
  'minutes',
  'seconds',
  'milliseconds',
]);

/**
 * Supported units for duration segmentation and presentation limits.
 */
export type TimeUnit = z.infer<typeof TimeUnitSchema>;

/*======================================================================
 * Configuration Defaults & Public Constants
 *======================================================================*/

/**
 * Default minimum precision for interval decomposition.
 */
export const DEFAULT_MIN_PRECISION: TimeUnit = 'milliseconds';

/**
 * Default maximum resolution for interval decomposition.
 */
export const DEFAULT_MAX_RESOLUTION: TimeUnit = 'years';

/**
 * Default largest unit for presentation interval decomposition (ADR 007).
 */
export const DEFAULT_LARGEST_UNIT: TimeUnit = 'years';

/**
 * Default smallest unit for presentation interval decomposition (ADR 007).
 */
export const DEFAULT_SMALLEST_UNIT: TimeUnit = 'seconds';

/**
 * Default maximum number of displayed units in the sliding window (ADR 007).
 */
export const DEFAULT_MAX_UNITS = 4;

/**
 * Default behavior for preserving leading zero units (ADR 007).
 */
export const DEFAULT_KEEP_LEADING_ZEROS = false;

/**
 * Default operational timing direction for temporal specifications.
 */
export const DEFAULT_TIMER_DIRECTION: TimerDirection = 'COUNT_DOWN';

/**
 * Minimum allowable character length for a timer title.
 */
export const MIN_TIMER_TITLE_LENGTH = 1;

/**
 * Maximum allowable character length for a timer title.
 */
export const MAX_TIMER_TITLE_LENGTH = 100;

/**
 * Maximum allowable character length for a call-to-action button label.
 */
export const MAX_CTA_LABEL_LENGTH = 30;

/**
 * Maximum allowable character length for an Event Notes accordion heading.
 */
export const MAX_EVENT_NOTES_HEADING_LENGTH = 50;

/**
 * Maximum allowable character length for a custom SEO description.
 */
export const MAX_SEO_DESCRIPTION_LENGTH = 320;

/**
 * Recommended minimum character length for search engine snippet optimization (SERP sweet spot).
 */
export const RECOMMENDED_SEO_DESCRIPTION_MIN_LENGTH = 120;

/**
 * Recommended maximum character length for search engine snippet optimization (SERP sweet spot).
 */
export const RECOMMENDED_SEO_DESCRIPTION_MAX_LENGTH = 160;

/*======================================================================
 * Duration Presentation & Timer State Interfaces
 *======================================================================*/

/**
 * Represents the structured components of a decomposed duration.
 */
export interface DecomposedInterval {
  /**
   * The number of years in the duration (approximated at 365 days).
   */
  years: number;

  /**
   * The number of months in the duration (approximated at 30 days).
   */
  months: number;

  /**
   * The number of days in the duration.
   */
  days: number;

  /**
   * The number of hours in the duration.
   */
  hours: number;

  /**
   * The number of minutes in the duration.
   */
  minutes: number;

  /**
   * The number of seconds in the duration.
   */
  seconds: number;

  /**
   * The number of milliseconds in the duration.
   */
  milliseconds: number;
}

/**
 * Canonical list of supported timer completion celebration types.
 */
export const CELEBRATION_TYPES = ['none', 'confetti'] as const;

/**
 * Default zero-crossing celebration effect.
 */
export const DEFAULT_CELEBRATION_TYPE = 'confetti' as const;

/**
 * Default visibility configuration for rendered Event Info on public display routes.
 */
export const DEFAULT_SHOW_EVENT_INFO = true;

/**
 * Default configuration for viral social sharing controls on public display routes.
 */
export const DEFAULT_SHOW_SOCIAL_SHARING = true;

/**
 * Default configuration for viewer reminder and alert subscription opt-in on public display routes.
 */
export const DEFAULT_SHOW_ALERTS = true;

/**
 * Default configuration for viewer like reaction button and counter on public display routes.
 */
export const DEFAULT_SHOW_LIKE_BUTTON = true;

/**
 * Default configuration for creator newsletter/updates opt-in checkbox on reminder subscription dialogs.
 */
export const DEFAULT_ENABLE_CREATOR_OPT_IN = false;

/**
 * Schema validating zero-crossing celebration animation effect types.
 */
export const CelebrationTypeSchema = z.enum(CELEBRATION_TYPES);

/**
 * Supported celebration animation and effect types.
 */
export type CelebrationType = z.infer<typeof CelebrationTypeSchema>;

/**
 * Schema validating a policy for filtering, clipping, and folding duration presentation.
 */
export const PresentationPolicySchema = z.object({
  /**
   * The largest time unit allowed. Larger units fold down into this unit (ADR 007).
   * @defaultValue {@link DEFAULT_LARGEST_UNIT}
   */
  largestUnit: TimeUnitSchema.optional(),

  /**
   * Legacy alias for largestUnit.
   * @defaultValue {@link DEFAULT_MAX_RESOLUTION}
   */
  maxResolution: TimeUnitSchema.optional(),

  /**
   * The smallest time unit allowed. Smaller units are omitted or clipped to 0 (ADR 007).
   * @defaultValue {@link DEFAULT_SMALLEST_UNIT}
   */
  smallestUnit: TimeUnitSchema.optional(),

  /**
   * Legacy alias for smallestUnit.
   * @defaultValue {@link DEFAULT_MIN_PRECISION}
   */
  minPrecision: TimeUnitSchema.optional(),

  /**
   * Maximum number of contiguous unit cards to display simultaneously (ADR 007).
   * Accepts 'all' (or null) for all available units, or an integer from 1 to 6.
   * @defaultValue {@link DEFAULT_MAX_UNITS}
   */
  maxUnits: z
    .union([z.number().int().min(1).max(6), z.literal('all')])
    .nullable()
    .optional(),

  /**
   * Whether to preserve leading zero units to maintain a fixed window size (ADR 007).
   * @defaultValue {@link DEFAULT_KEEP_LEADING_ZEROS}
   */
  keepLeadingZeros: z.boolean().optional(),

  /**
   * Deprecated flag for keeping seconds visible. Superseded by ADR 007 unified presentation.
   */
  forceSeconds: z.boolean().optional(),

  /**
   * Optional zero-crossing celebration animation effect type.
   * @defaultValue {@link DEFAULT_CELEBRATION_TYPE}
   */
  celebration: CelebrationTypeSchema.optional(),

  /**
   * Whether to visibly display the rendered Event Info on public countdown pages.
   *
   * @remarks
   * When `false`, the event description is suppressed from visible page layout,
   * but remains preserved for OpenGraph, Twitter, and SEO crawler metadata.
   *
   * @defaultValue {@link DEFAULT_SHOW_EVENT_INFO}
   */
  showEventInfo: z.boolean().optional(),

  /**
   * Whether to display social sharing controls (action bar and mobile share sheet) on public countdown pages.
   *
   * @remarks
   * When `false`, social sharing icons and action bars are suppressed from visible page layout.
   * Note that on the Free tier, sharing remains enabled to fuel viral attribution unless unlocked by a paid plan.
   *
   * @defaultValue {@link DEFAULT_SHOW_SOCIAL_SHARING}
   */
  showSocialSharing: z.boolean().optional(),

  /**
   * Whether to display viewer alert and reminder subscription controls on public countdown pages.
   *
   * @remarks
   * When `false`, reminder subscription slots and triggers are suppressed from visible page layout.
   *
   * @defaultValue {@link DEFAULT_SHOW_ALERTS}
   */
  showAlerts: z.boolean().optional(),

  /**
   * Whether to display viewer like reaction button and counter on public countdown pages.
   *
   * @remarks
   * When `false`, like reaction slots and triggers are suppressed from visible page layout.
   *
   * @defaultValue {@link DEFAULT_SHOW_LIKE_BUTTON}
   */
  showLikeButton: z.boolean().optional(),

  /**
   * Whether to include an opt-in checkbox for viewers to receive updates/news from the creator.
   *
   * @remarks
   * When `true`, the alert modal renders an additional checkbox allowing viewers to opt into
   * updates from the creator.
   *
   * @defaultValue {@link DEFAULT_ENABLE_CREATOR_OPT_IN}
   */
  enableCreatorOptIn: z.boolean().optional(),

  /**
   * Custom user-facing heading label for the collapsible Event Notes accordion.
   * If omitted, falls back to the default "Event Notes" label.
   */
  eventNotesHeading: z.preprocess((val) => {
    if (typeof val === 'string') {
      const cleaned = sanitize(val).trim();
      return cleaned.length > 0 ? cleaned : undefined;
    }
    return val;
  }, z.string().max(MAX_EVENT_NOTES_HEADING_LENGTH).optional()),
});

/**
 * Defines a policy for filtering, clipping, and folding duration presentation.
 */
export type PresentationPolicy = z.infer<typeof PresentationPolicySchema>;

/**
 * Represents the computed real-time state of an active timer at any evaluation point.
 */
export interface TimerState {
  /**
   * The calculated signed time delta in milliseconds between the anchor event and the evaluation point.
   *
   * @remarks
   * Formula: $T_{event} - T_{eval}$. A positive value indicates the target event is in the future.
   */
  deltaMs: number;

  /**
   * The current operational status of the timer.
   * @see {@link TimerStatus}
   */
  status: TimerStatus;

  /**
   * The remaining duration in milliseconds until the target event.
   *
   * @remarks
   * This is strictly non-negative. If the event has passed, it is capped at `0`.
   */
  remainingMs: number;

  /**
   * The elapsed duration in milliseconds since the target event passed.
   *
   * @remarks
   * This is strictly non-negative. If the event is in the future, it is capped at `0`.
   */
  elapsedMs: number;

  /**
   * Indicates whether the countdown has reached or passed its target event.
   *
   * @remarks
   * True if {@link TimerState.status} is `'COMPLETED'` (see {@link TimerStatus}) or the target event is in the past under countdown.
   */
  isExpired: boolean;

  /**
   * Indicates whether the timer is currently operating in auto-flipped count-up mode.
   */
  isAutoFlipped?: boolean;

  /**
   * Indicates whether the timer is currently within its post-zero active celebration grace period.
   */
  isGracePeriod?: boolean;

  /**
   * The remaining duration in milliseconds of an active event or celebration grace period.
   *
   * @remarks
   * Evaluated as $D_{active} - \text{elapsedMs}$ when within the active duration window.
   */
  remainingActiveMs?: number;

  /**
   * The decomposed presentation segments representing the remaining duration (for countdown)
   * or the elapsed duration (for count-up) under the configured presentation policy.
   */
  segments?: DecomposedInterval;
}

/*======================================================================
 * Sanitization Engine
 *======================================================================*/

/**
 * Pure-JS utility that removes executable HTML, embedding blocks, inline script
 * handlers, and script protocols from high-risk fields while preserving safe punctuation.
 *
 * @param input - The raw input string to sanitize.
 * @returns The sanitized plain-text string.
 */
export function sanitize(input: string): string {
  if (typeof input !== 'string') return input;

  let result = input;

  // 1. Strip script tags and their content
  result = result.replace(/<script\b[^>]*>([\s\S]*?)<\/script>/gi, '');
  result = result.replace(/<script\b[^>]*\/>/gi, '');

  // 2. Strip iframe tags and their content
  result = result.replace(/<iframe\b[^>]*>([\s\S]*?)<\/iframe>/gi, '');
  result = result.replace(/<iframe\b[^>]*\/>/gi, '');

  // 3. Strip any other HTML tags but preserve standalone < or > symbols (like '<3')
  result = result.replace(/<\/?[a-zA-Z][^>]*>/g, '');

  // 4. Strip inline event handlers (such as onerror, onload, onclick)
  result = result.replace(/\bon\w+\s*=\s*(['"][^'"]*['"]|\S+)/gi, '');

  // 5. Strip JavaScript (javascript:) protocol strings
  result = result.replace(/javascript\s*:\s*[^"'\s]*/gi, '');

  return result;
}

/*======================================================================
 * Color Validation & CSS Token Regular Expressions
 *======================================================================*/

/**
 * Standard Hex Color Code Regex.
 * Supports #rgb, #rgba, #rrggbb, and #rrggbbaa.
 */
const hexColorRegex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/**
 * Matches general safe CSS color words/names (letters, digits, dashes, and underscores).
 */
const cssColorWordRegex = /^[a-zA-Z0-9_-]+$/;

/**
 * Matches safe functional CSS notations like rgb(), rgba(), hsl(), hsla(), var(), and others,
 * while strictly rejecting potential bracket/markup or semicolon breakout scripts.
 */
const cssColorFunctionRegex = /^[a-zA-Z-]+\([^)]*\)$/;

/**
 * Validates whether a theme string is a valid CSS theme color or hexadecimal code.
 *
 * @param val - The color value to check.
 * @returns True if the value is a valid hex or CSS color definition.
 */
export function isValidCSSColor(val: string): boolean {
  if (typeof val !== 'string') return false;
  const normalized = val.trim();
  if (normalized.startsWith('#')) {
    return hexColorRegex.test(normalized);
  }
  return cssColorFunctionRegex.test(normalized) || cssColorWordRegex.test(normalized);
}

/*======================================================================
 * Zod Validation Schemas
 *======================================================================*/

/**
 * Schema validating the temporal aspects of a timer configuration.
 */
export const TemporalSpecSchema = z.object({
  /**
   * Target event anchor date-time as a valid ISO 8601 UTC timestamp.
   */
  anchorDate: z.iso.datetime(),

  /**
   * Standard IANA timezone string verified by Intl timezone compatibility.
   */
  timezone: z.string().refine(
    (tz) => {
      try {
        Intl.DateTimeFormat(undefined, { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Invalid IANA timezone' },
  ),

  /**
   * The operational timing direction.
   * @defaultValue {@link DEFAULT_TIMER_DIRECTION}
   */
  direction: z
    .enum(['COUNT_DOWN', 'COUNT_UP', 'AUTO_FLIP', 'PERIODIC'])
    .default(DEFAULT_TIMER_DIRECTION),
});

/*======================================================================
 * Declarative Theme Architecture & Styling Schemas
 *======================================================================*/

/**
 * Category classification for timer visual themes.
 */
export const ThemeCategorySchema = z.enum(['celebratory', 'athletic', 'technical', 'custom']);

/**
 * Supported structural visual card styling treatments.
 */
export const ThemeCardStyleSchema = z.enum(['minimal', 'neon', 'slab']);

/**
 * Supported decorative embellishments for unit cards and containers.
 */
export const ThemeAccentStyleSchema = z.enum(['none', 'confetti-dots', 'ribbon', 'athletic-trim']);

/**
 * Color tokens defining the visual palette for a theme.
 */
export const ThemeColorsSchema = z.object({
  /**
   * Main container or full viewport background color (Hex or CSS color).
   */
  background: z.string().refine(isValidCSSColor, {
    message: 'Invalid CSS background color or Hex code',
  }),

  /**
   * Card container surface background fill.
   */
  surface: z.string().refine(isValidCSSColor, {
    message: 'Invalid CSS surface color or Hex code',
  }),

  /**
   * Card border stroke color.
   */
  border: z.string().refine(isValidCSSColor, {
    message: 'Invalid CSS border color or Hex code',
  }),

  /**
   * Primary digit numeral text color.
   */
  primary: z.string().refine(isValidCSSColor, {
    message: 'Invalid CSS primary color or Hex code',
  }),

  /**
   * Optional secondary accent or trim color.
   */
  secondary: z
    .string()
    .refine(isValidCSSColor, {
      message: 'Invalid CSS secondary color or Hex code',
    })
    .optional(),

  /**
   * Unit label text color (DAYS, HOURS, MINUTES, SECONDS).
   */
  label: z.string().refine(isValidCSSColor, {
    message: 'Invalid CSS label color or Hex code',
  }),

  /**
   * Optional luminous glow or shadow accent color.
   */
  glow: z
    .string()
    .refine(isValidCSSColor, {
      message: 'Invalid CSS glow color or Hex code',
    })
    .optional(),
});

/**
 * Typography tokens configuring font family and weight hierarchies.
 */
export const ThemeTypographySchema = z.object({
  /**
   * Web-safe font family fallback string.
   */
  fontFamily: z.string().min(1, 'Font family cannot be empty'),

  /**
   * CSS font-weight for temporal digit numerals.
   * @defaultValue '700'
   */
  digitWeight: z.string().default('700'),

  /**
   * CSS font-weight for interval unit labels.
   * @defaultValue '600'
   */
  labelWeight: z.string().default('600'),

  /**
   * Optional CSS letter-spacing value.
   */
  letterSpacing: z.string().optional(),
});

/**
 * Presentational styling rules for segmented unit cards.
 */
export const ThemeCardConfigSchema = z.object({
  /**
   * Card visual style variant.
   * @defaultValue 'minimal'
   */
  style: ThemeCardStyleSchema.default('minimal'),

  /**
   * Border radius CSS value.
   * @defaultValue '16px'
   */
  borderRadius: z.string().default('16px'),

  /**
   * Border stroke width CSS value.
   * @defaultValue '1px'
   */
  borderWidth: z.string().default('1px'),

  /**
   * Optional CSS box-shadow string.
   */
  shadow: z.string().optional(),
});

/**
 * Decorative styling rules for festive or athletic embellishments.
 */
export const ThemeDecorationsSchema = z.object({
  /**
   * Decorative accent style flag.
   * @defaultValue 'none'
   */
  accentStyle: ThemeAccentStyleSchema.default('none'),
});

/**
 * Lowercase identifier regex for canonical theme IDs.
 */
const THEME_ID_REGEX = /^[a-z0-9-]+$/;

/**
 * Schema validating complete declarative theme specifications.
 */
export const ThemeDefinitionSchema = z.object({
  /**
   * Unique lowercase identifier (e.g. 'pastel-birthday', 'stadium-green', 'maize-blue').
   */
  id: z
    .string()
    .min(1, 'Theme ID cannot be empty')
    .regex(THEME_ID_REGEX, 'Theme ID must be lowercase alphanumeric with hyphens'),

  /**
   * Human-readable display label (e.g. 'Pastel Birthday', 'Maize & Blue').
   */
  name: z.string().min(1, 'Theme name cannot be empty'),

  /**
   * Theme category classification.
   */
  category: ThemeCategorySchema,

  /**
   * Color palette tokens.
   */
  colors: ThemeColorsSchema,

  /**
   * Typography tokens.
   */
  typography: ThemeTypographySchema,

  /**
   * Unit card presentation rules.
   */
  card: ThemeCardConfigSchema.default({
    style: 'minimal',
    borderRadius: '16px',
    borderWidth: '1px',
  }),

  /**
   * Decorative accents and trims.
   */
  decorations: ThemeDecorationsSchema.default({
    accentStyle: 'none',
  }),
});

/**
 * Schema validating visual styling, skin layout, and themes.
 *
 * @remarks
 * Supports named catalog theme IDs, legacy CSS hex colors, and inline custom ThemeDefinition objects.
 */
export const VisualSkinSchema = z.preprocess(
  (val: unknown) => {
    if (
      val &&
      typeof val === 'object' &&
      'theme' in val &&
      typeof (val as { theme: unknown }).theme === 'object' &&
      (val as { theme: unknown }).theme !== null
    ) {
      const obj = val as Record<string, unknown>;
      const inlineTheme = obj.theme as Record<string, unknown>;
      return {
        ...obj,
        themeDefinition: obj.themeDefinition ?? inlineTheme,
        theme: typeof inlineTheme.id === 'string' ? inlineTheme.id : 'custom',
      };
    }
    return val;
  },
  z.object({
    /**
     * Predefined skin layout styles.
     */
    layout: z.enum(['classic', 'minimal', 'compact', 'nixie']),

    /**
     * Named catalog theme ID or valid CSS color / Hex code string.
     */
    theme: z.string().refine(isValidCSSColor, {
      message: 'Invalid CSS theme color or Hex code',
    }),

    /**
     * Predefined list of safe web fonts.
     */
    font: z.string(),

    /**
     * Optional inline custom theme definition.
     */
    themeDefinition: ThemeDefinitionSchema.optional(),
  }),
);

/**
 * Supported display phases for call-to-action buttons.
 */
export const CtaDisplayPhaseSchema = z.enum(['always', 'before', 'after', 'disabled']);

/**
 * Display phase configuration for a call-to-action button.
 */
export type CtaDisplayPhase = z.infer<typeof CtaDisplayPhaseSchema>;

/**
 * Schema validating call-to-action interactive link targets.
 */
export const CallToActionSchema = z.object({
  /**
   * Standard URL button label. Maximum length is {@link MAX_CTA_LABEL_LENGTH} characters.
   */
  label: z.preprocess(
    (val) => (typeof val === 'string' ? sanitize(val) : val),
    z.string().min(1).max(MAX_CTA_LABEL_LENGTH),
  ),

  /**
   * Interactive destination URL, strictly enforcing HTTPS-only syntax.
   */
  url: z.url().refine((u) => u.startsWith('https://'), { message: 'URL must use HTTPS protocol' }),

  /**
   * Interactive link behavior flag.
   * @defaultValue true
   */
  openInNewTab: z.boolean().default(true),

  /**
   * Lifecycle phase in which this CTA button is active and rendered.
   *
   * @remarks
   * - `'always'`: Active and visible in both countdown and post-event phases.
   * - `'before'`: Active only before event start ($\Delta t > 0$).
   * - `'after'`: Active only after event start ($\Delta t \le 0$).
   * - `'disabled'`: Inactive/dormant; not displayed in either phase and does not count toward active quota.
   *
   * @defaultValue 'always'
   */
  displayPhase: CtaDisplayPhaseSchema.default('always'),
});

/**
 * Calculates the maximum number of simultaneously active CTA buttons at any point in time.
 *
 * @remarks
 * Evaluates active button counts across both pre-event (`always` + `before`) and post-event
 * (`always` + `after`) lifecycle phases. Buttons marked `'disabled'` are excluded from active counts.
 *
 * @param ctas - Array of call-to-action configurations.
 * @returns Maximum count of concurrent buttons visible at any moment.
 */
export function getMaxSimultaneousCtaCount(
  ctas?: Array<{ displayPhase?: 'always' | 'before' | 'after' | 'disabled' }>,
): number {
  if (!ctas || ctas.length === 0) return 0;
  let beforeCount = 0;
  let afterCount = 0;
  for (const cta of ctas) {
    const phase = cta.displayPhase ?? 'always';
    if (phase === 'disabled') {
      continue;
    }
    if (phase === 'always') {
      beforeCount++;
      afterCount++;
    } else if (phase === 'before') {
      beforeCount++;
    } else if (phase === 'after') {
      afterCount++;
    }
  }
  return Math.max(beforeCount, afterCount);
}

/**
 * Schema validating customizable phase titles for AUTO_FLIP timers across zero-crossing boundaries.
 */
export const PhaseTitlesSchema = z.object({
  /**
   * Pre-event phase title.
   * Displayed on the timer face while the countdown is actively ticking towards the target event.
   *
   * @example "Countdown to Quit Day", "Days Until Wedding", "Launch Countdown"
   */
  before: z.preprocess(
    (val) => (typeof val === 'string' ? sanitize(val) : val),
    z.string().min(MIN_TIMER_TITLE_LENGTH).max(MAX_TIMER_TITLE_LENGTH).optional(),
  ),

  /**
   * Active event / grace period phase title.
   * Displayed on the timer face while the event is ongoing (Live Now / during active window).
   *
   * @example "Event is Live!", "Keynote in Progress", "{title} Happening Now"
   */
  during: z.preprocess(
    (val) => (typeof val === 'string' ? sanitize(val) : val),
    z.string().min(MIN_TIMER_TITLE_LENGTH).max(MAX_TIMER_TITLE_LENGTH).optional(),
  ),

  /**
   * Post-event count-up phase title.
   * Displayed on the timer face once the event has arrived or passed and the timer has auto-flipped into count-up mode.
   *
   * @example "Sober For", "Happily Married For", "Product Live For"
   */
  after: z.preprocess(
    (val) => (typeof val === 'string' ? sanitize(val) : val),
    z.string().min(MIN_TIMER_TITLE_LENGTH).max(MAX_TIMER_TITLE_LENGTH).optional(),
  ),
});

/**
 * Customizable phase titles for AUTO_FLIP timers across zero-crossing boundaries.
 */
export type PhaseTitles = z.infer<typeof PhaseTitlesSchema>;

/**
 * Schema validating optional analytics integration properties.
 */
export const AnalyticsTrackingSchema = z.object({
  googleAnalyticsId: z.string().optional(),
  plausibleDomain: z.string().optional(),
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
});

/**
 * Central orchestrating schema for an event timer configuration instance.
 */
export const TimerConfigSchema = z.object({
  /**
   * Unique UUIDv4 or string-based URL-safe slug identifier.
   */
  id: z.string(),

  /**
   * Structured title string between {@link MIN_TIMER_TITLE_LENGTH} and {@link MAX_TIMER_TITLE_LENGTH} characters.
   */
  title: z.preprocess(
    (val) => (typeof val === 'string' ? sanitize(val) : val),
    z.string().min(MIN_TIMER_TITLE_LENGTH).max(MAX_TIMER_TITLE_LENGTH),
  ),

  /**
   * Comprehensive narrative description string.
   */
  description: z.preprocess(
    (val) => (typeof val === 'string' ? sanitize(val) : val),
    z.string().optional(),
  ),

  /**
   * Optional custom search engine and social media preview description.
   * Limited to {@link MAX_SEO_DESCRIPTION_LENGTH} characters.
   */
  seoDescription: z.preprocess(
    (val) => (typeof val === 'string' ? sanitize(val) : val),
    z.string().max(MAX_SEO_DESCRIPTION_LENGTH).optional(),
  ),

  /**
   * Custom URL-safe routing slug.
   */
  slug: z.preprocess(
    (val) => (typeof val === 'string' ? sanitize(val) : val),
    z.string().optional(),
  ),

  /**
   * Optional customizable phase titles for AUTO_FLIP timers (Phase 1 countdown vs. Phase 2 elapsed).
   */
  phaseTitles: PhaseTitlesSchema.optional(),

  /**
   * Temporal validation specification.
   */
  temporal: TemporalSpecSchema,

  /**
   * Optional presentation policy configuration.
   */
  presentation: PresentationPolicySchema.optional(),

  /**
   * Optional custom styling properties.
   */
  visual: VisualSkinSchema.optional(),

  /**
   * Optional interactive destination link buttons.
   */
  cta: z.array(CallToActionSchema).optional(),

  /**
   * Optional tracking and custom utm integrations.
   */
  tracking: AnalyticsTrackingSchema.optional(),

  /**
   * Optional target event specification (e.g. relative calendar recurrence, astronomical, duration).
   */
  targetSpec: TargetSpecSchema.optional(),

  /**
   * Optional active celebration grace period duration (ISO 8601 string or numeric ms).
   */
  periodicActiveDuration: z.union([z.string(), z.number()]).optional(),

  /**
   * Optional contextual status label for active event grace periods (e.g. 'Happening now', 'In progress').
   */
  duringLabel: z.string().optional(),

  /**
   * Optional contextual status label for concluded events in the after phase (e.g. 'Ended', 'Concluded').
   */
  afterLabel: z.string().optional(),

  /**
   * Optional contextual status label for active event grace periods (e.g. 'LIVE NOW', 'IN PROGRESS').
   */
  activeLabel: z.string().optional(),

  /**
   * Author-configured sharing setting indicating whether this timer may be cloned,
   * forked, or hydrated as a reusable template by other viewers.
   *
   * @defaultValue false
   */
  allowRemix: z.boolean().default(false),
});

/*======================================================================
 * Schema-Inferred TypeScript Types
 *======================================================================*/

export type TemporalSpec = z.infer<typeof TemporalSpecSchema>;
export type VisualSkin = z.infer<typeof VisualSkinSchema>;
export type CallToAction = Omit<z.infer<typeof CallToActionSchema>, 'displayPhase'> & {
  displayPhase?: CtaDisplayPhase;
};
export type AnalyticsTracking = z.infer<typeof AnalyticsTrackingSchema>;
export type TimerConfig = Omit<z.infer<typeof TimerConfigSchema>, 'allowRemix' | 'cta'> & {
  allowRemix?: boolean;
  cta?: CallToAction[];
};

export type ThemeCategory = z.infer<typeof ThemeCategorySchema>;
export type ThemeCardStyle = z.infer<typeof ThemeCardStyleSchema>;
export type ThemeAccentStyle = z.infer<typeof ThemeAccentStyleSchema>;
export type ThemeColors = z.infer<typeof ThemeColorsSchema>;
export type ThemeTypography = z.infer<typeof ThemeTypographySchema>;
export type ThemeCardConfig = z.infer<typeof ThemeCardConfigSchema>;
export type ThemeDecorations = z.infer<typeof ThemeDecorationsSchema>;
export type ThemeDefinition = z.infer<typeof ThemeDefinitionSchema>;

/*======================================================================
 * Public Discovery & Category Taxonomy Schemas
 *======================================================================*/

/**
 * Schema validating dynamic category catalog definitions.
 */
export const CategoryDefinitionSchema = z.object({
  /**
   * Unique category identifier slug (e.g., 'holidays', 'tech', 'gaming').
   */
  id: z.string().min(1, 'Category ID cannot be empty'),

  /**
   * Human-readable display label (e.g., 'Holidays & Seasons').
   */
  label: z.string().min(1, 'Category label cannot be empty'),

  /**
   * Optional category description.
   */
  description: z.string().nullable().optional(),

  /**
   * Optional icon token or identifier (e.g., 'gift', 'laptop').
   */
  icon: z.string().nullable().optional(),

  /**
   * Numeric sort order for catalog presentation.
   */
  sortOrder: z.number().int('Sort order must be an integer'),

  /**
   * Operational active status flag.
   */
  isActive: z.boolean().default(true),

  /**
   * Creation timestamp.
   */
  createdAt: z.union([z.date(), z.number(), z.string()]).optional(),

  /**
   * Update timestamp.
   */
  updatedAt: z.union([z.date(), z.number(), z.string()]).optional(),
});

/**
 * Dynamic category catalog item definition.
 */
export type CategoryDefinition = z.infer<typeof CategoryDefinitionSchema>;

/*======================================================================
 * User Account Preferences Schema (PL2-06)
 *======================================================================*/

/**
 * Valid theme mode preference options for user accounts.
 */
export const USER_THEME_PREFERENCE_VALUES = ['system', 'light', 'dark'] as const;
export type UserThemePreference = (typeof USER_THEME_PREFERENCE_VALUES)[number];

/**
 * Zod schema defining persistent user account preferences.
 */
export const UserAccountPreferencesSchema = z.object({
  /**
   * Theme mode preference: 'system' (ambient preference), 'light', or 'dark'.
   */
  themePreference: z.enum(USER_THEME_PREFERENCE_VALUES).default('system'),

  /**
   * Default IANA timezone for newly created timers (e.g. 'America/New_York', 'UTC').
   */
  defaultTimezone: z.string().min(1).default('UTC'),
});

/**
 * Typed user account preferences structure.
 */
export type UserAccountPreferences = z.infer<typeof UserAccountPreferencesSchema>;

/**
 * Default fallback user account preferences.
 */
export const DEFAULT_USER_ACCOUNT_PREFERENCES: UserAccountPreferences = Object.freeze({
  themePreference: 'system',
  defaultTimezone: 'UTC',
});

/**
 * Validates and parses user account preferences, applying canonical defaults for omitted properties.
 *
 * @param input - Raw preferences input to validate.
 * @returns Clean, validated {@link UserAccountPreferences}.
 */
export function parseUserAccountPreferences(input: unknown): UserAccountPreferences {
  return UserAccountPreferencesSchema.parse(input ?? {});
}

/*======================================================================
 * Stub Implementation Helpers
 *======================================================================*/

/**
 * Throws an implementation-related error indicating that the logic is deferred.
 *
 * @param message - The optional specific details about what is not yet implemented.
 * @throws An {@link Error} specifying that the operation is a stub.
 */
export const todo = (message?: string): never => {
  throw new Error(message ? `TODO: ${message}` : 'An operation is not implemented.');
};

export * from './pricing.js';
export * from './categories.js';
export * from './relative-calendar.js';
export * from './feedback.js';
